import {
  ApolloClient,
  NormalizedCacheObject,
  QueryOptions,
  OperationVariables,
} from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetUnassignedEntitiesDocument } from "~/graphql/supergraphHooks";
import {
  type GetUnassignedEntitiesQuery,
  type ClientArenaFilter,
  type ClientArenaSort,
} from "~/graphql/supergraphTypes";
import type { EntityRowItem } from "./types";

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): ClientArenaFilter[] | null => {
  if (!filterModel) {
    return null;
  }
  if ("filterType" in filterModel) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: ClientArenaFilter[] = [];
  for (const [field, filterInfo] of Object.entries(filterModel)) {
    if (filterInfo.filterType === "text" && typeof filterInfo.filter === "string") {
      queryFilters.push({ field, filterValues: [filterInfo.filter] });
    }
  }
  return queryFilters.length > 0 ? queryFilters : null;
};

export const getSortVars = (
  sortItems: AgGrid.SortModelItem[] | null | undefined,
): ClientArenaSort[] | null => {
  if (!sortItems || sortItems.length === 0) {
    return null;
  }
  return sortItems.map((item) => ({
    field: item.colId,
    sortAscending: item.sort === "asc",
  }));
};

export const rowsFromQuery = (
  entities: GetUnassignedEntitiesQuery["getUnassignedEntities"]["entities"] | null | undefined,
): EntityRowItem[] => {
  if (!entities) {
    return [];
  }
  return entities.map((entity) => ({
    entityId: entity.entityId,
    entityName: entity.entityName,
    softwareEumUsd: entity.softwareEumUsd ?? 0,
    billableFaEumUsd: entity.billableFaEumUsd ?? 0,
    jsqProductLine: entity.jsqProductLine ?? null,
    billingStatus: entity.billingStatus ?? "Unknown",
  }));
};

export type EntitiesGraphqlDataSourceProps = {
  onFiltersChange?: (filters: ClientArenaFilter[] | null) => void;
  onSortsChange?: (sorts: ClientArenaSort[] | null) => void;
};

export class EntitiesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  asOfDate: string;
  private onFiltersChange?: (filters: ClientArenaFilter[] | null) => void;
  private onSortsChange?: (sorts: ClientArenaSort[] | null) => void;

  constructor(
    props?: EntitiesGraphqlDataSourceProps,
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
    asOfDate: string = "",
  ) {
    this.client = apolloClient;
    this.asOfDate = asOfDate;
    this.onFiltersChange = props?.onFiltersChange;
    this.onSortsChange = props?.onSortsChange;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetUnassignedEntitiesQuery,
  ): void {
    const rowData = rowsFromQuery(response.getUnassignedEntities.entities);
    const rowCount = response.getUnassignedEntities.filteredCount;
    const startRow = params.request.startRow ?? 0;

    params.success({
      rowData,
      rowCount,
    });

    // Handle no-rows overlay for server-side row model
    // Only show overlay when on first page to avoid showing it on non-first pages
    if (rowCount === 0 && startRow === 0) {
      params.api?.showNoRowsOverlay();
    } else if (rowCount > 0) {
      params.api?.hideOverlay();
    }
  }

  public buildQueryOptions(
    params: AgGrid.IServerSideGetRowsParams,
  ): QueryOptions<OperationVariables, GetUnassignedEntitiesQuery> {
    const filters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const calculatedLimit = (params.request.endRow ?? 0) - offset;
    const limit = calculatedLimit <= 0 ? 10 : calculatedLimit;

    if (this.onFiltersChange) {
      this.onFiltersChange(filters);
    }

    if (this.onSortsChange) {
      this.onSortsChange(sorts);
    }

    return {
      query: GetUnassignedEntitiesDocument,
      variables: {
        asOfDate: this.asOfDate,
        offset: offset,
        limit: limit,
        filters: filters,
        sorts: sorts,
      },
      fetchPolicy: "no-cache",
    };
  }

  public async executeQuery(
    params: AgGrid.IServerSideGetRowsParams,
    queryOptions: ReturnType<typeof this.buildQueryOptions>,
  ): Promise<void> {
    try {
      const response = await this.client.query<GetUnassignedEntitiesQuery>(queryOptions);
      this.handleSuccess(params, response.data);
    } catch {
      this.handleError(params);
    }
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const queryOptions = this.buildQueryOptions(params);
    void this.executeQuery(params, queryOptions);
  }
}
